import { useThree } from '@react-three/fiber'
import { FC, useEffect, useRef } from 'react'
import { Box3, Sphere, Vector3 } from 'three'

/**
 * Component that auto-fits the camera to view the loaded tiles
 */
export const AutoFitCamera: FC<{
  tilesRenderer: unknown
  skipAutoFit?: boolean
}> = ({ tilesRenderer, skipAutoFit }) => {
  const { camera, controls } = useThree()
  const hasFitted = useRef(false)

  useEffect(() => {
    if (!tilesRenderer || hasFitted.current || skipAutoFit) return

    const renderer = tilesRenderer as {
      getBoundingSphere?: (target: Sphere) => Sphere
      getBoundingBox?: (target: Box3) => Box3
      addEventListener?: (event: string, callback: () => void) => void
      removeEventListener?: (event: string, callback: () => void) => void
    }

    const fitToView = () => {
      if (hasFitted.current) return

      const sphere = new Sphere()
      if (renderer.getBoundingSphere) {
        renderer.getBoundingSphere(sphere)

        if (sphere.radius > 0 && sphere.radius < Infinity) {
          hasFitted.current = true

          // Position camera to view the model
          const distance = sphere.radius * 2.5
          camera.position.set(
            sphere.center.x + distance * 0.5,
            sphere.center.y + distance * 0.5,
            sphere.center.z + distance
          )
          camera.lookAt(sphere.center)

          // Update orbit controls target
          if (controls && 'target' in controls) {
            const orbitControls = controls as { target: Vector3 }
            orbitControls.target.copy(sphere.center)
          }
        }
      }
    }

    // Try to fit immediately if data is ready
    fitToView()

    // Also listen for tile load events
    if (renderer.addEventListener) {
      renderer.addEventListener('load-tile-set', fitToView)
      renderer.addEventListener('tiles-load-end', fitToView)
    }

    return () => {
      if (renderer.removeEventListener) {
        renderer.removeEventListener('load-tile-set', fitToView)
        renderer.removeEventListener('tiles-load-end', fitToView)
      }
    }
  }, [tilesRenderer, camera, controls, skipAutoFit])

  return null
}
